import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Trash2, 
  Edit, 
  Upload, 
  X, 
  Search,
  Filter,
  Image as ImageIcon
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Card, CardContent } from '../components/ui/card';
import { getImages, uploadImage, updateImage, deleteImage, getCategories } from '../lib/api';
import { toast } from 'sonner';

export default function AdminImagesPage() {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Upload Dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    category: '',
    tags: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const fileInputRef = useRef(null);

  // Edit Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    category: '',
    tags: ''
  });

  // Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingImage, setDeletingImage] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const fetchData = async () => {
    try {
      const [imagesData, categoriesData] = await Promise.all([
        getImages(),
        getCategories()
      ]);
      setImages(imagesData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredImages = images.filter((image) => {
    const matchesSearch = searchTerm === '' ||
      image.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      image.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || image.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // File Selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setSelectedFiles(files);
    setPreviewUrls(files.map((file) => URL.createObjectURL(file)));
  };

  // Upload
  const handleUpload = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    if (!uploadData.category) {
      toast.error('Please select a category');
      return;
    }

    setUploadLoading(true);

    const toTitle = (value) => value
      .replace(/\.[^/.]+$/, '')
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());

    let successCount = 0;

    try {
      for (const [index, file] of selectedFiles.entries()) {
        const formData = new FormData();
        formData.append('file', file);

        const customBaseTitle = uploadData.title.trim();
        const categoryBaseTitle = toTitle(uploadData.category || 'Image');
        const resolvedTitle = selectedFiles.length === 1
          ? (customBaseTitle || toTitle(file.name))
          : (customBaseTitle
            ? `${customBaseTitle} ${index + 1}`
            : `${categoryBaseTitle} ${index + 1}`);

        formData.append('title', resolvedTitle || `Image ${index + 1}`);
        formData.append('description', uploadData.description);
        formData.append('category', uploadData.category);
        formData.append('tags', uploadData.tags);

        await uploadImage(formData);
        successCount += 1;
      }

      toast.success(
        successCount === 1
          ? 'Image uploaded successfully!'
          : `${successCount} images uploaded successfully!`
      );
      setUploadDialogOpen(false);
      resetUploadForm();
      fetchData();
    } catch (error) {
      console.error('Upload failed:', error);
      if (successCount > 0) {
        toast.warning(`${successCount} image(s) uploaded before failure.`);
      }
      toast.error(error.response?.data?.detail || 'Failed to upload image(s)');
    } finally {
      setUploadLoading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadData({ title: '', description: '', category: '', tags: '' });
    setSelectedFiles([]);
    setPreviewUrls([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Edit
  const openEditDialog = (image) => {
    setEditingImage(image);
    setEditData({
      title: image.title,
      description: image.description || '',
      category: image.category,
      tags: image.tags?.join(', ') || ''
    });
    setEditDialogOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditLoading(true);

    try {
      await updateImage(editingImage.id, {
        title: editData.title,
        description: editData.description,
        category: editData.category,
        tags: editData.tags.split(',').map(t => t.trim()).filter(Boolean)
      });
      toast.success('Image updated successfully!');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Update failed:', error);
      toast.error('Failed to update image');
    } finally {
      setEditLoading(false);
    }
  };

  // Delete
  const openDeleteDialog = (image) => {
    setDeletingImage(image);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteImage(deletingImage.id);
      toast.success('Image deleted successfully!');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete image');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-semibold mb-1">Images</h1>
            <p className="text-muted-foreground">{images.length} images in your gallery</p>
          </div>
          <Button onClick={() => setUploadDialogOpen(true)} data-testid="upload-button">
            <Plus className="h-4 w-4 mr-2" />
            Upload Image
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search images..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="admin-search-input"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-48" data-testid="admin-category-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Images Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="aspect-square bg-muted" />
                <CardContent className="p-4 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredImages.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <AnimatePresence>
              {filteredImages.map((image, index) => (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="overflow-hidden group" data-testid={`admin-image-${image.id}`}>
                    <div className="relative aspect-square">
                      <img
                        src={image.thumbnail_url}
                        alt={image.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          size="icon"
                          variant="secondary"
                          onClick={() => openEditDialog(image)}
                          data-testid={`edit-image-${image.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="destructive"
                          onClick={() => openDeleteDialog(image)}
                          data-testid={`delete-image-${image.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-medium truncate">{image.title}</h3>
                      <p className="text-sm text-muted-foreground capitalize">{image.category}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <Card className="p-12 text-center">
            <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">No images found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || filterCategory !== 'all'
                ? 'Try adjusting your filters'
                : 'Upload your first image to get started'}
            </p>
            <Button onClick={() => setUploadDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Upload Image
            </Button>
          </Card>
        )}

        {/* Upload Dialog */}
        <Dialog
          open={uploadDialogOpen}
          onOpenChange={(open) => {
            setUploadDialogOpen(open);
            if (!open) {
              resetUploadForm();
            }
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Upload Image</DialogTitle>
              <DialogDescription>
                Add a new image to your gallery. Images will be automatically optimized.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleUpload} className="space-y-4">
              {/* File Input */}
              <div 
                className={`
                  border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
                  transition-colors hover:border-primary
                  ${previewUrls.length > 0 ? 'border-primary' : 'border-border'}
                `}
                onClick={() => fileInputRef.current?.click()}
                data-testid="file-drop-zone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
                {previewUrls.length > 0 ? (
                  <div className="relative space-y-3">
                    <img src={previewUrls[0]} alt="Preview" className="max-h-48 mx-auto rounded" />
                    <p className="text-sm text-muted-foreground">
                      {selectedFiles.length} file{selectedFiles.length === 1 ? '' : 's'} selected
                    </p>
                    <Button
                      type="button"
                      size="icon"
                      variant="destructive"
                      className="absolute top-2 right-2"
                      onClick={(e) => { e.stopPropagation(); resetUploadForm(); }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Click to select an image or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      JPEG, PNG, or WebP (max 10MB)
                    </p>
                  </>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="upload-title">Title {selectedFiles.length > 1 ? '(optional for bulk)' : '*'}</Label>
                <Input
                  id="upload-title"
                  value={uploadData.title}
                  onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                  required={selectedFiles.length <= 1}
                  data-testid="upload-title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="upload-description">Description</Label>
                <Textarea
                  id="upload-description"
                  value={uploadData.description}
                  onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                  rows={3}
                  data-testid="upload-description"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="upload-category">Category *</Label>
                <Select
                  value={uploadData.category}
                  onValueChange={(value) => setUploadData({ ...uploadData, category: value })}
                  required
                >
                  <SelectTrigger data-testid="upload-category">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="upload-tags">Tags (comma-separated)</Label>
                <Input
                  id="upload-tags"
                  value={uploadData.tags}
                  onChange={(e) => setUploadData({ ...uploadData, tags: e.target.value })}
                  placeholder="e.g., colorful, vibrant, digital"
                  data-testid="upload-tags"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={uploadLoading || selectedFiles.length === 0} data-testid="upload-submit">
                  {uploadLoading ? 'Uploading...' : selectedFiles.length > 1 ? 'Upload All' : 'Upload'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Image</DialogTitle>
              <DialogDescription>
                Update the details of this image.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleEdit} className="space-y-4">
              {editingImage && (
                <div className="aspect-video relative rounded-lg overflow-hidden">
                  <img
                    src={editingImage.thumbnail_url}
                    alt={editingImage.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="edit-title">Title *</Label>
                <Input
                  id="edit-title"
                  value={editData.title}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  required
                  data-testid="edit-title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  rows={3}
                  data-testid="edit-description"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-category">Category *</Label>
                <Select
                  value={editData.category}
                  onValueChange={(value) => setEditData({ ...editData, category: value })}
                >
                  <SelectTrigger data-testid="edit-category">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-tags">Tags (comma-separated)</Label>
                <Input
                  id="edit-tags"
                  value={editData.tags}
                  onChange={(e) => setEditData({ ...editData, tags: e.target.value })}
                  placeholder="e.g., colorful, vibrant, digital"
                  data-testid="edit-tags"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={editLoading} data-testid="edit-submit">
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Image?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{deletingImage?.title}"? 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deleteLoading}
                data-testid="confirm-delete"
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
